import { mapActions, mapState } from "vuex";
import { PERIOD } from "@/enums/period.enum";
import ViewHeader from "@/components/ui/ViewHeader";
import SectionLoader from "@/components/Loader/SectionLoader.vue";
import DoughnutChart from "@/components/Analytics/GMBAddresses/chart/Doughnut.js";
import BarChart from "@/components/Analytics/GMBAddresses/chart/Bar.js";
import FilterDialog from "@/components/Filter/dialog";
import LastAudit from "@/components/ui/ExpansionPanel/Header/LastAudit.vue";
import NoData from "@/components/ui/NoData";
import moment from "moment";
import { FORMAT } from "@/config/date-format";
import serviceHandler from "@/helpers/handlers/service";
import ErrorHandler from "@/helpers/handlers/error";
import facilitiesService from "@/services/facilities";
import { gtmEvent__filterDatePeriod } from "@/helpers/gtm-events";
import { GMB_DATA_ENUM, GMB_EXPORT_GROUP_BY } from "@/enums/gmbData.enum";
import { EXPORT_FILE_EXTENSION } from "@/enums/file-type";
import { COMPARE_PERIODS_ENUM } from "@/enums/comparePeriods.enum";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";
import UsersService from "@/services/users";
import ConversionCharts from "@/components/Analytics/GMBAddresses/chartCards/ConversionChartsGroup.vue";
import AbsoluteCharts from "@/components/Analytics/GMBAddresses/chartCards/AbsoluteChartsGroup.vue";
import ChartTitleTooltip from "@/components/Analytics/GMBAddresses/chartCards/ChartTitleTooltip.vue";

export const gmbAddressMixin = {
  components: {
    ViewHeader,
    SectionLoader,
    DoughnutChart,
    BarChart,
    FilterDialog,
    LastAudit,
    NoData,
    ConversionCharts,
    AbsoluteCharts,
    ChartTitleTooltip
  },
  data() {
    return {
      exportId: null,
      FEATURE_FLAG,
      viewTitle: this.$t("message.analytics.businessAddresses.title"),
      tooltipMaxWidth: 500,
      loading: {
        view: false,
        exportBtn: false
      },
      infoIconColor: "lime",

      editDates: false,
      dateInputType: null,
      menu: false,
      comparePeriodItems: [
        {
          text: `${this.$t("message.analytics.businessAddresses.noCompare")}`,
          value: COMPARE_PERIODS_ENUM.NO_COMPARE
        },
        {
          text: `${this.$t(
            "message.analytics.businessAddresses.compareWithPreviousPeriod"
          )}`,
          value: COMPARE_PERIODS_ENUM.COMPARE_WITH_PREVIOUS_PERIOD
        },
        {
          text: `${this.$t(
            "message.analytics.businessAddresses.compareWithPreviousYear"
          )}`,
          value: COMPARE_PERIODS_ENUM.COMPARE_WITH_PREVIOUS_YEAR
        }
      ],
      comparePeriodValue: null,
      charts: null,
      loaded: false,
      filterDialog: false,
      cities: [],
      gmbAddressesIds: [],
      facilitiesGroupsIds: [],
      searchType: null,
      showConversion: true,
      EXPORT_FILE_EXTENSION,
      GMB_EXPORT_GROUP_BY,
      isDateInRange: true
    };
  },

  created() {
    if (!isFeatureFlagEnabled(FEATURE_FLAG.SHOW_CONVERSION_CHARTS)) {
      this.showConversion = false;
    }
  },

  async mounted() {
    this.setParams();
    this.comparePeriodValue = COMPARE_PERIODS_ENUM.NO_COMPARE;
    await this.makeGMBAddresses();

    this.scrollToHash();
    this.mixin__setGtmEngagementTimeouts();
  },

  destroyed() {
    this.resetFacilitiesStore();
    this.resetFacilitiesGroupsStore();
  },

  computed: {
    ...mapState({
      allFacilitiesIds: state => state.facilities.allFacilitiesIds,
      selectedLocale: state => state.settings.selectedLocale
    }),
    dateRangeText() {
      if (this.date.length > 1) {
        return this.date.join(" ~ ");
      }
      return this.date[0];
    },
    exportDataPayload() {
      return {
        dateFrom: this.date[0],
        dateTo: this.date[1] || this.date[0],
        compareEnum: this.comparePeriodValue,
        gmbDataEnum:
          this.comparePeriodValue !== COMPARE_PERIODS_ENUM.NO_COMPARE
            ? GMB_DATA_ENUM.GMB_WITH_COMPARISON_DATA
            : GMB_DATA_ENUM.GMB_SIMPLE_DATA
      };
    }
  },

  watch: {
    date(newDate) {
      if (newDate[0] && this.dateFrom !== newDate[0]) {
        this.dateFrom = newDate[0];
      }
      if (newDate[1] && this.dateTo !== newDate[1]) {
        this.dateTo = newDate[1];
      }

      this.dateInputType = "calendar";
    }
  },

  methods: {
    ...mapActions({
      resetFacilitiesStore: "facilities/resetStore",
      resetFacilitiesGroupsStore: "facilitiesGroups/resetStore"
    }),
    isFeatureFlagEnabled,
    checkDateRange() {
      const today = new Date();
      const eighteenMonthsAgo = new Date();
      eighteenMonthsAgo.setMonth(today.getMonth() - 18);

      this.isDateInRange = this.date.some(dateStr => {
        const checkDate = new Date(dateStr);
        return checkDate > eighteenMonthsAgo;
      });
    },
    async makeGMBAddresses(initFacilities = true) {
      this.loading.view = true;
      try {
        await this.gmbAddressesFacade.makeGMBAddresses(
          {
            dateFrom: this.date[0],
            dateTo: this.date[1] || this.date[0],
            compareEnum: this.comparePeriodValue,
            gmbDataEnum:
              this.comparePeriodValue !== COMPARE_PERIODS_ENUM.NO_COMPARE
                ? GMB_DATA_ENUM.GMB_WITH_COMPARISON_DATA
                : GMB_DATA_ENUM.GMB_SIMPLE_DATA
          },
          initFacilities
        );
      } catch (error) {
        ErrorHandler.displayError(error);
      }
      this.charts = this.gmbAddressesFacade.returnCharts();
      this.loading.view = false;
    },

    async exportGMBAddressesData(downloadFileExtension, groupBy) {
      this.loading.exportBtn = true;
      try {
        await this.gmbAddressesFacade.exportGMBAddressesData(
          {
            ...this.exportDataPayload,
            groupBy
          },
          downloadFileExtension
        );
      } catch (error) {
        ErrorHandler.displayError(error);
      }
      this.loading.exportBtn = false;
    },

    async exportGMBAddressesDataAsync(downloadFileExtension, groupBy) {
      this.loading.exportBtn = true;
      try {
        const {
          export_id
        } = await this.gmbAddressesFacade.exportGMBAddressesDataAsync(
          {
            ...this.exportDataPayload,
            groupBy
          },
          downloadFileExtension
        );
        this.exportId = export_id;
        this.showAsyncDownloadDialog = true;
      } catch (error) {
        ErrorHandler.displayError(error);
      }
      this.loading.exportBtn = false;
    },

    async selectedAddressesUpdate(addressesIds) {
      this.filterDialog = false;

      await this.$store.dispatch(
        "facilities/selectFacilitiesByGmbAddressIds",
        addressesIds
      );

      await this.makeGMBAddresses(false);
    },

    async handleDatePickerOk() {
      if (this.date.length === 1) {
        this.date = [this.date[0], this.date[0]];
      }
      this.$refs.menu.save(this.date);

      gtmEvent__filterDatePeriod({
        event: GTM_EVENT_NAME.FILTER_DATE_PERIOD,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        dateInputType: this.dateInputType,
        compareWith: Object.keys(COMPARE_PERIODS_ENUM)
          .find(key => COMPARE_PERIODS_ENUM[key] === this.comparePeriodValue)
          .toLowerCase()
      });

      await this.makeGMBAddresses(false);
      this.checkDateRange();
    },

    goToGMBAccounts() {
      this.$router.push({ name: "profile-gmb-accounts" });
    },

    setParams() {
      if (this.$route.query.dateFrom && this.$route.query.dateTo) {
        this.date = [this.$route.query.dateFrom, this.$route.query.dateTo];
      }
    },

    async fetchFacilitiesIds() {
      const params = {
        isOwner: true,
        cities: this.cities,
        groups: this.facilitiesGroupsIds
      };

      if (this.searchType === "facilities" && this.gmbAddressesIds.length) {
        this.selectedAddressesUpdate(this.gmbAddressesIds);
      } else {
        const addressesIds = await serviceHandler(
          facilitiesService.getFacilitiesIds(params)
        );

        this.selectedAddressesUpdate(
          addressesIds.map(ids => ids.gmb_address_id)
        );
      }
    },

    async onFiltersChange({
      gmbAddressesIds = this.gmbAddressesIds,
      cities = this.cities,
      facilitiesGroupsIds = this.facilitiesGroupsIds
    }) {
      this.loading.view = true;
      this.page = 1;

      this.gmbAddressesIds = gmbAddressesIds;
      this.cities = cities;
      this.facilitiesGroupsIds = facilitiesGroupsIds;

      await this.fetchFacilitiesIds();

      this.loading.view = false;
    },

    handleEditDatesBtn() {
      if (this.editDates) {
        this.setDates();
      }
      this.editDates = !this.editDates;
    },
    checkIfDateIsValid(date) {
      return moment(date, FORMAT.DATE, true).isValid();
    },
    async setDates() {
      const oldDate = this.date;

      if (!this.checkIfDateIsValid(this.dateFrom)) {
        this.dateFrom = oldDate[0];
      }
      if (!this.checkIfDateIsValid(this.dateTo)) {
        this.dateTo = oldDate[1];
      }
      if (
        this.checkIfDateIsValid(this.dateFrom) &&
        this.checkIfDateIsValid(this.dateTo)
      ) {
        this.date = [this.dateFrom, this.dateTo];
      }

      await this.$nextTick(() => {
        this.dateInputType = "manual";
      });
    },

    closeFilterDialog() {
      this.filterDialog = false;
    }
  }
};
